import React from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { Box } from '@mui/material'

import { useAuth } from './contexts/AuthContext'
import Layout from './components/Layout'
import Login from './pages/Login'
import Register from './pages/Register'
import VerifyEmail from './pages/VerifyEmail'
import ResetPassword from './pages/ResetPassword'
import ForgotPassword from './pages/ForgotPassword'
import Dashboard from './pages/Dashboard'
import HealthData from './pages/HealthData'
import Profile from './pages/Profile'
import TrialSearches from './pages/TrialSearches'
import Landing from './pages/Landing'
import Privacy from './pages/Privacy'
import Terms from './pages/Terms'
import About from './pages/About'
import AIDataEntry from './pages/features/AIDataEntry'
import HealthProfile from './pages/features/HealthProfile'
import TrialSearch from './pages/features/TrialSearch'
import PrivacySecurity from './pages/features/PrivacySecurity'
import LoadingSpinner from './components/LoadingSpinner'

// Protected Route component
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading } = useAuth()

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <LoadingSpinner />
      </Box>
    )
  }

  return user ? <>{children}</> : <Navigate to="/login" />
}

// Public Route component (redirect if authenticated)
const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading } = useAuth()

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <LoadingSpinner />
      </Box>
    )
  }

  return !user ? <>{children}</> : <Navigate to="/dashboard" />
}

const App: React.FC = () => {
  const { user, loading } = useAuth()

  return (
    <Routes>
      {/* Public landing and legal pages */}
      <Route path="/" element={user && !loading ? <Navigate to="/dashboard" /> : <Landing />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/about" element={<About />} />
      
      {/* Feature pages */}
      <Route path="/features/ai-data-entry" element={<AIDataEntry />} />
      <Route path="/features/health-profile" element={<HealthProfile />} />
      <Route path="/features/trial-search" element={<TrialSearch />} />
      <Route path="/features/privacy-security" element={<PrivacySecurity />} />
      
      {/* Auth routes */}
      <Route 
        path="/login" 
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        } 
      />
      <Route 
        path="/register" 
        element={
          <PublicRoute>
            <Register />
          </PublicRoute>
        } 
      />
      <Route 
        path="/forgot-password" 
        element={
          <PublicRoute>
            <ForgotPassword />
          </PublicRoute>
        } 
      />
      <Route path="/verify-email/:token" element={<VerifyEmail />} />
      <Route path="/reset-password/:token" element={<ResetPassword />} />
      
      {/* Protected routes */}
      <Route 
        path="/app" 
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Navigate to="/app/dashboard" replace />} />
        <Route path="dashboard" element={<Dashboard />} />
        <Route path="health-data" element={<HealthData />} />
        <Route path="trial-searches" element={<TrialSearches />} />
        <Route path="profile" element={<Profile />} />
      </Route>
      <Route path="/dashboard" element={<Navigate to="/app/dashboard" replace />} />
      <Route path="/health-data" element={<Navigate to="/app/health-data" replace />} />
      <Route path="/trial-searches" element={<Navigate to="/app/trial-searches" replace />} />
      <Route path="/profile" element={<Navigate to="/app/profile" replace />} />
      
      {/* Catch all route */}
      <Route path="*" element={<Navigate to="/" />} />
    </Routes>
  )
}

export default App
